import { usePortalDropdown } from '../usePortalDropdown';

/**
 * Shared open/close-on-outside-click/geometry state for a `document.body`-
 * portaled dropdown (the field picker and person picker both use this) — a
 * thin wrapper over the shared `usePortalDropdown` (Unit 3 dropdown-
 * positioning-logic promotion, plan
 * `plans/2026-09-22-contact-actions-intelligence-implementation-plan.md`
 * §1 D9 / §4). This hook's own 4 consumers (`FieldPicker`,
 * `PersonValuePicker`, `PersonMultiValuePicker`, `MultiValueInput`) take
 * `menuStyle` straight from here now (plan U8 — one positioning subscription
 * per picker, not two): they used to ALSO each call `useAnchoredPosition`
 * themselves for geometry, duplicating the one `usePortalDropdown` already
 * computes internally — two ResizeObservers and two scroll/resize listener
 * sets per open picker for no behavioral difference. Callers that need a
 * non-default horizontal anchor pass `{ align: 'right' }`, threaded straight
 * through to `usePortalDropdown`/`useAnchoredPosition`.
 *
 * `swallowDismissClick: true` reproduces this hook's pre-promotion behavior
 * exactly (bug, Spencer report 2026-08-13, round 2): a click that closes
 * this menu is fully consumed rather than also reaching whatever it landed
 * on (e.g. the hosting `ResponsivePopover`'s own "Apply filters" button, if
 * this menu happened to be covering it) — outside-click and Escape layering
 * (so a nested picker closes before the `ResponsivePopover` hosting it does)
 * live in `usePortalDropdown` itself now; see that hook's doc comment for
 * the full mechanics (`outside-click-stack.ts` / `ui/escape-stack.ts`).
 */
export function useDropdownAnchor(opts: { align?: 'left' | 'right' } = {}) {
  const { open, setOpen, triggerRef, menuRef, menuStyle } = usePortalDropdown<HTMLDivElement, HTMLDivElement>({
    swallowDismissClick: true,
    align: opts.align,
  });
  return { open, setOpen, triggerRef, menuRef, menuStyle };
}
